import {useCallback, useEffect, useState} from 'react'
import axios from 'axios'
import {useSnackbar} from 'notistack'
import {apiConstants} from '../../common/constants'

/**
 * Centralized hook for the Bulk Serviceability module.
 * Wraps all 11 API endpoints and exposes typed response handling.
 *
 * The hook returns helper methods only — components decide when to call them
 * and manage their own loading state if needed for finer-grained UX.
 */

const BASE = apiConstants.BULK_SERV_BASE // 'order/serviceability'
const ROOT = apiConstants.BASE_LOCAL + BASE // full URL prefix

function asUrl(path) {
  return `${apiConstants.BASE_LOCAL}${path}`
}

export const PINCODE_TYPES = {
  PICKUP_PREPAID: 'PICKUP_PREPAID',
  DROP_PREPAID: 'DROP_PREPAID',
  DROP_COD: 'DROP_COD',
}

export const PINCODE_TYPE_LABELS = {
  PICKUP_PREPAID: 'Pickup Prepaid',
  DROP_PREPAID: 'Drop Prepaid',
  DROP_COD: 'Drop COD',
}

export const PINCODE_TYPE_HELPERS = {
  PICKUP_PREPAID: 'Pincodes from which courier can pick up',
  DROP_PREPAID: 'Delivery pincodes for prepaid orders',
  DROP_COD: 'Delivery pincodes for COD orders',
}

// B2B (PTL) couriers keep a pickup and a drop list only (no COD drop list); B2B allocation reads them.
export const SHIPMENT_TYPE_B2B = 'B2B'
const B2B_PINCODE_TYPE_LABELS = {
  PICKUP_PREPAID: 'Pickup',
  DROP_PREPAID: 'Drop',
}
const B2B_PINCODE_TYPE_HELPERS = {
  PICKUP_PREPAID: 'Pincodes the courier can pick up from',
  DROP_PREPAID: 'Pincodes the courier can deliver to',
}

/** Pincode types offered for a shipment type, with their labels and helper text. */
export function pincodeTypesFor(shipmentType) {
  if (shipmentType === SHIPMENT_TYPE_B2B) {
    return {
      types: [PINCODE_TYPES.PICKUP_PREPAID, PINCODE_TYPES.DROP_PREPAID],
      // COD keeps its label: history / audit rows of B2B codes uploaded on the Bulk Serviceability page can carry it
      labels: {...PINCODE_TYPE_LABELS, ...B2B_PINCODE_TYPE_LABELS},
      helpers: B2B_PINCODE_TYPE_HELPERS,
    }
  }
  return {types: Object.values(PINCODE_TYPES), labels: PINCODE_TYPE_LABELS, helpers: PINCODE_TYPE_HELPERS}
}

// PD (pickup→drop combo) serviceability for SDD/NDD — its own service-type + payment-mode dimensions.
export const PD_SERVICE_TYPES = {SDD: 'SDD', NDD: 'NDD'}
export const PD_PAYMENT_MODES = {prepaid: 'prepaid', cod: 'cod'}

export const ACTION_TYPE_LABELS = {
  BULK_UPLOAD: 'Bulk Upload',
  PD_BULK_UPLOAD: 'PD Upload (SDD/NDD)',
  L1_TOGGLE: 'L1 Toggle',
  L1_SCOPE_UPDATE: 'L1 Scope Update',
  DEACTIVATE: 'Deactivate',
}

export const STATUS_COLORS = {
  COMPLETED: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
}

function extractError(err, fallback = 'Something went wrong') {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  )
}

/**
 * shipmentType: 'B2B' scopes the parent / account-code / audit-log lists to B2B (PTL) couriers
 * (PTL Operations → PTL Serviceability). Left out = every courier, as the Bulk Serviceability page.
 */
export function useBulkServiceability({shipmentType} = {}) {
  const {enqueueSnackbar} = useSnackbar()
  const scope = shipmentType ? {shipmentType} : {}

  // 3.1 GET /parent-cps
  const fetchParentCps = useCallback(async () => {
    try {
      const res = await axios.get(asUrl(apiConstants.BULK_SERV_PARENT_CPS), {params: scope})
      return res?.data?.parentCps || []
    } catch (err) {
      enqueueSnackbar(extractError(err, 'Failed to load parent couriers'), {variant: 'error'})
      return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, shipmentType])

  // 3.2 GET /account-codes?parent_cp=
  const fetchAccountCodes = useCallback(
    async (parentCp) => {
      if (!parentCp) return null
      try {
        const res = await axios.get(asUrl(apiConstants.BULK_SERV_ACCOUNT_CODES), {
          params: {parent_cp: parentCp, ...scope},
        })
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Failed to load account codes'), {variant: 'error'})
        return null
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, shipmentType]
  )

  // 3.3 POST /upload?confirm=false  (dry-run)
  const dryRunUpload = useCallback(
    async (file) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await axios.post(`${ROOT}/upload?confirm=false`, formData, {
          headers: {'Content-Type': 'multipart/form-data'},
        })
        return res?.data || null
      } catch (err) {
        const errorCode = err?.response?.data?.errorCode
        if (errorCode === 'CSV_NO_VALID_PINCODES') {
          enqueueSnackbar('No valid pincodes found in the uploaded file', {variant: 'error'})
        } else {
          enqueueSnackbar(extractError(err, 'CSV validation failed'), {variant: 'error'})
        }
        return null
      }
    },
    [enqueueSnackbar]
  )

  // 3.4 POST /upload?confirm=true (fan-out upload)
  const confirmUpload = useCallback(
    async ({file, courierParent, pincodeType, courierAccountCodes}) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('courierParent', courierParent)
        formData.append('pincodeType', pincodeType)
        // Send each account code as a separate form field so Spring binds them
        // to a List<String> instead of receiving a single JSON-encoded string.
        ;(courierAccountCodes || []).forEach((code) =>
          formData.append('courierAccountCodes', code)
        )
        const res = await axios.post(`${ROOT}/upload?confirm=true`, formData, {
          headers: {'Content-Type': 'multipart/form-data'},
        })
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Upload failed'), {variant: 'error'})
        return null
      }
    },
    [enqueueSnackbar]
  )

  // 3.5 GET /{accountCode}/status
  const fetchAccountCodeStatus = useCallback(
    async (accountCode) => {
      try {
        const res = await axios.get(`${ROOT}/${encodeURIComponent(accountCode)}/status`)
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Failed to fetch status'), {variant: 'error'})
        return null
      }
    },
    [enqueueSnackbar]
  )

  // 3.6 GET /{accountCode}/history
  const fetchAccountCodeHistory = useCallback(
    async (accountCode) => {
      try {
        const res = await axios.get(`${ROOT}/${encodeURIComponent(accountCode)}/history`)
        return res?.data?.uploadHistory || []
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Failed to fetch history'), {variant: 'error'})
        return []
      }
    },
    [enqueueSnackbar]
  )

  // 3.7 GET /{accountCode}/download?pincodeType=
  const downloadAccountCodeCsv = useCallback(
    async (accountCode, pincodeType) => {
      try {
        const res = await axios.get(
          `${ROOT}/${encodeURIComponent(accountCode)}/download`,
          {params: {pincodeType}, responseType: 'blob'}
        )
        const blob = new Blob([res.data], {type: 'text/csv;charset=utf-8'})
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${accountCode}_${pincodeType}.csv`
        link.click()
        URL.revokeObjectURL(url)
        return true
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Download failed'), {variant: 'error'})
        return false
      }
    },
    [enqueueSnackbar]
  )

  // 3.8 GET /audit-log
  const fetchAuditLog = useCallback(
    async (params = {}) => {
      try {
        const res = await axios.get(asUrl(apiConstants.BULK_SERV_AUDIT_LOG), {
          params: {page: 0, size: 20, ...params, ...scope},
        })
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Failed to load audit log'), {variant: 'error'})
        return null
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, shipmentType]
  )

  // 3.9 PATCH /{accountCode}/l1-toggle
  const toggleL1 = useCallback(
    async (accountCode, l1Enabled) => {
      try {
        const res = await axios.patch(
          `${ROOT}/${encodeURIComponent(accountCode)}/l1-toggle`,
          {l1Enabled}
        )
        enqueueSnackbar(res?.data?.message || 'L1 toggle updated', {variant: 'success'})
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Failed to toggle L1'), {variant: 'error'})
        return null
      }
    },
    [enqueueSnackbar]
  )

  // 3.10 PATCH /{accountCode}/l1-merchants
  const updateMerchantScope = useCallback(
    async (accountCode, body) => {
      try {
        const res = await axios.patch(
          `${ROOT}/${encodeURIComponent(accountCode)}/l1-merchants`,
          body
        )
        enqueueSnackbar(res?.data?.message || 'Merchant scope updated', {variant: 'success'})
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'Failed to update merchant scope'), {variant: 'error'})
        return null
      }
    },
    [enqueueSnackbar]
  )

  // 3.11 DELETE /{accountCode}  (PROSHIPADMIN only)
  const deactivateAccountCode = useCallback(
    async (accountCode) => {
      try {
        const res = await axios.delete(`${ROOT}/${encodeURIComponent(accountCode)}`)
        enqueueSnackbar(res?.data?.message || 'Deactivated', {variant: 'success'})
        return res?.data || null
      } catch (err) {
        if (err?.response?.status === 403) {
          enqueueSnackbar("You don't have permission to deactivate", {variant: 'error'})
        } else {
          enqueueSnackbar(extractError(err, 'Failed to deactivate'), {variant: 'error'})
        }
        return null
      }
    },
    [enqueueSnackbar]
  )

  // PD (SDD/NDD pickup→drop pair) dry-run — 2-column CSV (pickup_pincode,drop_pincode)
  const dryRunPdUpload = useCallback(
    async (file) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await axios.post(`${ROOT}/pd/upload?confirm=false`, formData, {
          headers: {'Content-Type': 'multipart/form-data'},
        })
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'PD CSV validation failed'), {variant: 'error'})
        return null
      }
    },
    [enqueueSnackbar]
  )

  // PD fan-out upload — replaces pd_serviceability for each (courier, serviceType, paymentMode)
  const confirmPdUpload = useCallback(
    async ({file, courierParent, serviceType, paymentMode, courierAccountCodes}) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('courierParent', courierParent)
        formData.append('serviceType', serviceType)
        formData.append('paymentMode', paymentMode)
        ;(courierAccountCodes || []).forEach((code) =>
          formData.append('courierAccountCodes', code)
        )
        const res = await axios.post(`${ROOT}/pd/upload?confirm=true`, formData, {
          headers: {'Content-Type': 'multipart/form-data'},
        })
        return res?.data || null
      } catch (err) {
        enqueueSnackbar(extractError(err, 'PD upload failed'), {variant: 'error'})
        return null
      }
    },
    [enqueueSnackbar]
  )

  return {
    fetchParentCps,
    fetchAccountCodes,
    dryRunUpload,
    confirmUpload,
    dryRunPdUpload,
    confirmPdUpload,
    fetchAccountCodeStatus,
    fetchAccountCodeHistory,
    downloadAccountCodeCsv,
    fetchAuditLog,
    toggleL1,
    updateMerchantScope,
    deactivateAccountCode,
  }
}

/* Convenience self-fetching hooks for primary lists ─────────────────────── */

export function useParentCps(shipmentType) {
  const {fetchParentCps} = useBulkServiceability({shipmentType})
  const [parentCps, setParentCps] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  useEffect(() => {
    let mounted = true
    setIsLoading(true)
    fetchParentCps().then((data) => {
      if (mounted) {
        setParentCps(data)
        setIsLoading(false)
      }
    })
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return {parentCps, isLoading}
}
